import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getAccountReps, getBrokers, createAccountRep } from "../../services/firestore";
import "./OwnerLayout.css";

export default function AccountReps() {
  const { userProfile } = useAuth();
  const [reps, setReps] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [brokerFilter, setBrokerFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    Promise.all([
      getAccountReps(userProfile.companyId),
      getBrokers(userProfile.companyId),
    ]).then(([r, b]) => {
      setReps(r);
      setBrokers(b);
      setLoading(false);
    });
  }, [userProfile]);

  const filtered = reps.filter((r) => {
    const matchSearch = !search || r.name?.toLowerCase().includes(search.toLowerCase()) || r.phone?.includes(search);
    const matchBroker = brokerFilter === "all" || r.brokerId === brokerFilter;
    return matchSearch && matchBroker;
  });

  if (selected) {
    const broker = brokers.find((b) => b.id === selected.brokerId);
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div className="page-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button className="btn btn-sm" onClick={() => setSelected(null)}>← Back</button>
            <div>
              <div className="page-title">{selected.name}</div>
              <div className="page-subtitle">{broker?.name || "—"}</div>
            </div>
          </div>
        </div>
        <div className="page-body">
          <div className="two-col">
            <div className="panel">
              <div className="panel-header"><div className="panel-title">Contact</div></div>
              <div className="field-row">
                <div className="field-label">Phone</div>
                <div className="field-value">
                  {selected.phone ? <a href={`tel:${selected.phone}`} style={{ color: "#185FA5" }}>{selected.phone}</a> : <span className="field-value-muted">—</span>}
                </div>
              </div>
              <div className="field-row">
                <div className="field-label">Email</div>
                <div className="field-value">{selected.email || <span className="field-value-muted">—</span>}</div>
              </div>
              <div className="field-row">
                <div className="field-label">Broker</div>
                <div className="field-value">{broker?.name || "—"}</div>
              </div>
              <div className="field-row">
                <div className="field-label">Typical locations</div>
                <div className="field-value">{selected.locations || <span className="field-value-muted">—</span>}</div>
              </div>
            </div>
            <div className="panel">
              <div className="panel-header"><div className="panel-title">Notes</div></div>
              <div style={{ padding: 14 }}>
                <div style={{ fontSize: 12, color: "#555", whiteSpace: "pre-wrap" }}>{selected.notes || <span style={{ color: "#bbb" }}>No notes</span>}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Account reps</div>
          <div className="page-subtitle">{reps.length} rep{reps.length !== 1 ? "s" : ""}</div>
        </div>
        <button className="btn btn-blue" onClick={() => setShowAdd(true)}>+ Add rep</button>
      </div>

      <div className="page-body">
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <input
            style={{ width: 260 }}
            placeholder="Search reps..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={brokerFilter} onChange={(e) => setBrokerFilter(e.target.value)} style={{ fontSize: 12 }}>
            <option value="all">All brokers</option>
            {brokers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🤝</div>
            <div>{search || brokerFilter !== "all" ? "No reps match" : "No account reps yet"}</div>
          </div>
        ) : (
          <div className="panel">
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 140px 120px 60px",
              padding: "7px 14px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8",
              fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
            }}>
              <span>Name</span><span>Broker</span><span>Phone</span><span>Jobs</span>
            </div>
            {filtered.map((rep) => {
              const broker = brokers.find((b) => b.id === rep.brokerId);
              return (
                <div key={rep.id} onClick={() => setSelected(rep)} style={{
                  display: "grid", gridTemplateColumns: "1fr 140px 120px 60px",
                  padding: "10px 14px", borderBottom: "1px solid #f0f0f0",
                  alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer",
                }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#fafbfc"}
                  onMouseLeave={(e) => e.currentTarget.style.background = ""}
                >
                  <div style={{ fontWeight: 600 }}>{rep.name}</div>
                  <span className="chip chip-blue" style={{ fontSize: 10 }}>{broker?.name || "—"}</span>
                  <span style={{ color: "#666" }}>{rep.phone || "—"}</span>
                  <span style={{ color: "#888" }}>{rep.jobCount || 0}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAdd && (
        <AddRepModal
          companyId={userProfile?.companyId}
          brokers={brokers}
          onClose={() => setShowAdd(false)}
          onCreated={(rep) => { setReps((r) => [...r, rep]); setShowAdd(false); }}
        />
      )}
    </div>
  );
}

function AddRepModal({ companyId, brokers, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", brokerId: "", locations: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.name.trim()) return setError("Name is required.");
    setSaving(true);
    try {
      const ref = await createAccountRep({ ...form, companyId, jobCount: 0 });
      onCreated({ id: ref.id, ...form, jobCount: 0 });
    } catch {
      setError("Failed to save. Please try again.");
    }
    setSaving(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 420, boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Add account rep</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" }}>✕</button>
        </div>
        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
          {error && <div style={{ background: "#fff0f0", border: "1px solid #f5a5a5", borderRadius: 7, padding: "8px 10px", fontSize: 11, color: "#a32d2d" }}>{error}</div>}
          {[
            { label: "Full name *", key: "name" },
            { label: "Phone", key: "phone", type: "tel" },
            { label: "Email", key: "email", type: "email" },
            { label: "Typical locations", key: "locations" },
          ].map(({ label, key, type = "text" }) => (
            <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>{label}</label>
              <input type={type} value={form[key]} onChange={(e) => set(key, e.target.value)} />
            </div>
          ))}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Broker</label>
            <select value={form.brokerId} onChange={(e) => set("brokerId", e.target.value)}>
              <option value="">Select broker...</option>
              {brokers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} style={{ minHeight: 60 }} />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-blue" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Add rep"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
